import React, { useState, useMemo } from 'react';
import PropTypes from 'prop-types';
import { useMutation, useQuery } from '@apollo/client';
import Modal from '../../../../components/Modal';
import TextInput from '../../../../components/TextInput';
import {
  updateCategory,
  updateStoreSuperAdminMutation,
} from '../../../../graphql/mutations';
import Button from '../../../../components/Button';
import {
  Field,
  ModalInner,
  NoImgSelection,
  PlaceholderSelectionImg,
} from './style';
import { categoryPlaceholderList } from './placeholderList';
import { HeaderSmall } from '../../../../components/Text';
import Draggable from '../../../../components/Draggable';
import { changeOrderHelper } from '@/utils/data';

import Checkbox from '../../../../components/Checkbox';
import {
  productCategories as productCategoriesQuery,
  adminInfoStoreQuery,
} from '../../../../graphql/queries';
import {
  CheckBoxLabel,
  CheckBoxLabelContainer,
} from '../../../../components/Checkbox/pickpackCheckbox';
import i18n from '@/utils/i18n';
import ClassicLoadingSpinner from '../../../../components/LoadingSpinner/ClassicLoadingSpinner';

function CategoryEditModal({ category, storeId, onClose }) {
  const [values, setValues] = useState(category);
  const [updateCategoryMutation, { loading: updatingCategory }] =
    useMutation(updateCategory);
  const [updateStore, { loading: updatingStore }] = useMutation(
    updateStoreSuperAdminMutation,
  );
  const [initalStoreLayout, steInitalStoreLayout] = useState([]);
  const [store, setStore] = useState({});
  useQuery(adminInfoStoreQuery, {
    variables: { id: storeId },
    onCompleted: (d) => {
      const data = {
        ...d.store,
        store_layout_vending_machines: JSON.parse(
          d.store.store_layout_vending_machines_json || '{}',
        ),
      };
      setStore(data);
      steInitalStoreLayout([...data.store_layout]);
    },
  });

  const touchedValues = useMemo(() => {
    let touched = Object.keys(values).reduce((touched, key) => {
      if (category[key] !== values[key])
        return { ...touched, [key]: values[key] };
      return touched;
    }, {});
    if (
      JSON.stringify(store.store_layout) !== JSON.stringify(initalStoreLayout)
    ) {
      touched = {
        ...touched,
        store_layout: store.store_layout,
      };
    }
    if (
      JSON.stringify(store.store_layout_vending_machines) !==
      store.store_layout_vending_machines_json
    ) {
      touched = {
        ...touched,
        store_layout_vending_machines_json: JSON.stringify(
          store.store_layout_vending_machines,
        ),
      };
    }
    return touched;
  }, [category, values, store]);

  function handleChange({ target: { type, name, value } }) {
    const formattedValue =
      type === 'number' || type === 'select-one' ? +value : value;
    setValues({ ...values, [name]: formattedValue });
  }
  function handleChangeManually(newVal, name) {
    setValues({ ...values, [name]: newVal });
  }

  function handleShowCategoryVendingChange(vendingMachineId) {
    const vendingMachineCategories = toggleShowCategory(
      store.store_layout_vending_machines[vendingMachineId] || [],
    );
    const storeLayoutVendingMachines = {
      ...store.store_layout_vending_machines,
      [vendingMachineId]: vendingMachineCategories,
    };
    setStore({
      ...store,
      store_layout_vending_machines: storeLayoutVendingMachines,
    });
  }

  function handleShowCategoryStoreChange() {
    const storeLayout = toggleShowCategory(store.store_layout);
    setStore({
      ...store,
      store_layout: storeLayout,
    });
  }

  function toggleShowCategory(layout) {
    const index = layout.findIndex((id) => id === category.id);
    if (index === -1) {
      layout.push(category.id);
    } else {
      layout.splice(index, 1);
    }
    return layout;
  }

  function handleSave() {
    if (Object.keys(touchedValues).length) {
      const storeUpdate = {};
      if (touchedValues.store_layout != null) {
        storeUpdate.store_layout = touchedValues.store_layout;
      }
      if (touchedValues.store_layout_vending_machines_json != null) {
        storeUpdate.store_layout_vending_machines_json =
          touchedValues.store_layout_vending_machines_json;
      }
      if (Object.keys(storeUpdate).length > 0) {
        console.log(storeUpdate);
        updateStore({
          variables: {
            id: storeId,
            store: storeUpdate,
          },
          refetchQueries: [
            {
              query: adminInfoStoreQuery,
              variables: {
                id: storeId,
              },
            },
          ],
          update: () => {
            onClose();
          },
        });
      }
      const itemDirty = { ...touchedValues };
      const { store_layout, store_layout_vending_machines_json, ...item } =
        itemDirty;
      if (item.products) {
        item.products = item.products.map((product) => product.id);
      }
      if (item.published != null) {
        item.reusable_packaging = item.published;
      }
      if (Object.keys(item).length > 0) {
        console.log(item);
        updateCategoryMutation({
          variables: {
            id: category.id,
            item,
          },
          refetchQueries: [
            {
              query: productCategoriesQuery,
              variables: {
                id: storeId,
                filter: 'ALL',
              },
            },
          ],
          update: () => {
            onClose();
          },
        });
      }
    }
  }

  function sortProduct(productId, targetProductId) {
    setValues({
      ...values,
      products: changeOrderHelper(
        values.products,
        'id',
        productId,
        targetProductId,
      ),
    });
  }

  function onDragStartProduct(event) {
    event.dataTransfer.setData('text/plain', event.target.id);
  }

  function onDragOverProduct(event) {
    event.preventDefault();
  }

  function onDropProduct(event) {
    const productIdToMove = event.dataTransfer.getData('text');
    sortProduct(productIdToMove, event.target.id);
    event.dataTransfer.clearData();
  }

  if (store.id == null) {
    return <ClassicLoadingSpinner />;
  }

  return (
    <Modal onClose={onClose}>
      <ModalInner>
        <Field>
          <pre>{category.id}</pre>
        </Field>
        {values.icon ? (
          <img
            src={values.icon}
            alt={category.name}
            width="50px"
            height="50px"
            className="tw:h-[50px] tw:w-[50px]"
          />
        ) : (
          <div>{i18n.t('Menu:noPlaceholder')}</div>
        )}
        <Field>
          <HeaderSmall>{i18n.t('Menu:placeholderIcon')}</HeaderSmall>
          <div>
            {categoryPlaceholderList.map((placeholderUrl) => (
              <PlaceholderSelectionImg
                src={placeholderUrl}
                alt={placeholderUrl}
                selected={placeholderUrl === values.icon}
                onClick={() =>
                  setValues({
                    ...values,
                    icon: placeholderUrl,
                    placeholder: placeholderUrl,
                  })
                }
              />
            ))}
            <NoImgSelection
              selected={values.icon === null}
              onClick={() =>
                setValues({ ...values, icon: null, placeholder: null })
              }
            >
              {i18n.t('Menu:none')}
            </NoImgSelection>
          </div>
        </Field>
        <Field></Field>
        <Field>
          <TextInput
            placeholder={i18n.t('Menu:categoryName')}
            name="name"
            value={values.name}
            onChange={handleChange}
          />
        </Field>

        <HeaderSmall>{i18n.t('Menu:categoryToggles')}</HeaderSmall>
        <Field>
          <CheckBoxLabelContainer
            onClick={() => handleChangeManually(!values.published, 'published')}
          >
            <Checkbox checked={values.published}></Checkbox>
            <CheckBoxLabel>{i18n.t('Menu:published')}</CheckBoxLabel>
          </CheckBoxLabelContainer>
        </Field>

        <Field>
          <HeaderSmall>{i18n.t('Menu:changeProductOrder')}</HeaderSmall>
          {(values.products || []).map((product) => (
            <Draggable
              key={product.id}
              id={product.id}
              draggable
              onDragStart={onDragStartProduct}
              onDragOver={onDragOverProduct}
              onDrop={onDropProduct}
            >
              {product.name}
            </Draggable>
          ))}
        </Field>
        <Field>
          <CheckBoxLabelContainer onClick={handleShowCategoryStoreChange}>
            <Checkbox
              checked={(store.store_layout || []).includes(category.id)}
            ></Checkbox>
            <CheckBoxLabel>{i18n.t('Menu:showStore')}</CheckBoxLabel>
          </CheckBoxLabelContainer>
          {(store.vending_machines || []).map((vnd) => (
            <CheckBoxLabelContainer
              onClick={() => handleShowCategoryVendingChange(vnd.id)}
            >
              <Checkbox
                checked={(
                  store.store_layout_vending_machines[vnd.id] || []
                ).includes(category.id)}
              ></Checkbox>
              <CheckBoxLabel>
                {i18n.t('Menu:showVending', { name: vnd.name })}
              </CheckBoxLabel>
            </CheckBoxLabelContainer>
          ))}
        </Field>

        <Button
          onClick={handleSave}
          loading={updatingCategory || updatingStore}
          disabled={!Object.keys(touchedValues).length}
        >
          {i18n.t('Menu:save')}
        </Button>
      </ModalInner>
    </Modal>
  );
}

CategoryEditModal.propTypes = {
  category: PropTypes.object.isRequired,
  onClose: PropTypes.func.isRequired,
  storeId: PropTypes.string.isRequired,
};

export default CategoryEditModal;
